// "Homeworks" widget — the design system's HwCard: one glass card with a header
// (file icon + "Ödevler" + see-all) and a vertical list of horizontal
// subject-coded rows (pill + [kind rail / subject / topic] + progress bar + "X/Y" +
// due + CTA).
// Prefix: qak-. Subject tint/accent are applied inline from SubjectUtilization.
export const homeworkStyles = `
.qak-root {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  /* Solid (no blur) — one of ~10 card shells stacked on the scrolling
     dashboard grid; blur only frosts the flat page bg (pure raster cost). */
  background: var(--glass-flat-solid);
  border: 1px solid var(--glass-flat-border);
  box-shadow: var(--glass-flat-edge), var(--glass-flat-float-rest);
  border-radius: var(--rad-catchup-large);
  padding: var(--space-5);
}

.qak-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-3);
}
.qak-title-group {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}
.qak-title-group img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}
.qak-title {
  color: var(--text-primary);
  font-weight: var(--font-weight-bold);
  font-size: var(--fs-2xl);
}
.qak-see-all {
  cursor: pointer;
}
.qak-see-all-text {
  color: var(--catchup-blue-600);
  font-weight: var(--font-weight-bold);
  white-space: nowrap;
}

.qak-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
/* one homework row */
.qak-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4) var(--space-3) var(--space-3);
  border-radius: var(--rad-catchup-large);
  border: 1px solid var(--glass-flat-border);
  box-shadow: var(--glass-flat-edge), var(--glass-flat-float-rest);
  transition: transform var(--duration-base) var(--ease-standard), box-shadow var(--duration-base) var(--ease-standard);
}
.qak-item:hover {
  transform: translateY(-2px);
  box-shadow: var(--glass-flat-edge), var(--glass-flat-float-hover);
}
.qak-item-pill {
  width: 52px;
  height: 52px;
  object-fit: contain;
  flex-shrink: 0;
}
.qak-item-body {
  flex: 1 1 0%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
/* Meta rail — [kind eyebrow] … [status tag], the body's first row. Mirrors
   .qdd-hw-rail on the /homeworks card so the kind reads the same on every surface, but
   WITHOUT its hairline: this body is inset past the 52px emblem, so a rule here would
   span only part of the row.
   margin-left:auto on the status rather than justify-content:space-between — the kind
   renders null for a type outside the ödev table, and space-between would then park the
   status on the LEFT. (.qak-item-top, a space-between wrapper that existed only to hold
   the meta beside the status, is gone with the status's move.) */
.qak-item-rail {
  display: flex;
  align-items: center;
  gap: var(--space-2) var(--space-3);
  flex-wrap: wrap;
}
.qak-item-rail > .qak-status {
  margin-left: auto;
}
.qak-item-meta {
  min-width: 0;
}
/* Ders label. Plain text line since the kind eyebrow moved to .qak-item-rail — it no
   longer shares this row, so the flex/gap/wrap it carried for the chip would be dead
   declarations. Still inline-level (a span element), so the explicit display: block is
   what makes the topic below it sit on its own line. */
.qak-item-subject {
  display: block;
  font-weight: var(--font-weight-bold);
  font-size: var(--fs-lg);
  line-height: 1.2;
}
.qak-item-topic {
  display: block;
  font-size: var(--fs-md);
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qak-status {
  flex-shrink: 0;
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-bold);
  padding: 2px 9px;
  border-radius: var(--rad-catchup-full);
  border: 1px solid transparent;
  white-space: nowrap;
}
.qak-status--done {
  background: var(--color-success-bg);
  border-color: var(--color-success-border);
  color: var(--color-success-text);
}
.qak-status--ongoing {
  background: var(--brand-50);
  border-color: var(--color-info-border);
  color: var(--brand-dark);
}
.qak-status--new {
  background: var(--accent-coral-bg);
  border-color: var(--accent-coral-border);
  color: var(--accent-coral-text);
}
.qak-status--timeup {
  background: var(--color-error-bg);
  border-color: var(--color-error-border);
  color: var(--color-error-text);
}
/* Upcoming / not-yet-open — neutral slate "locked" chip, clearly not the actionable
   coral "new" and free of every subject color (Science/Chemistry/Philosophy are violet). */
.qak-status--upcoming {
  background: var(--catchup-gray-50);
  border-color: var(--catchup-gray-100);
  color: var(--catchup-gray-600);
}
/* Expired / window-closed — soft-red "ended" chip (shares the error palette with
   timeup), so a past-due assignment reads as closed, not actionable. */
.qak-status--expired {
  background: var(--color-error-bg);
  border-color: var(--color-error-border);
  color: var(--color-error-text);
}

.qak-bar {
  height: 7px;
  border-radius: var(--rad-catchup-full);
  background: rgba(90, 155, 170, 0.18);
  overflow: hidden;
}
.qak-bar-fill {
  height: 100%;
  border-radius: var(--rad-catchup-full);
  box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.4);
  transition: width var(--duration-slow) var(--ease-standard);
}
.qak-item-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}
.qak-item-sub {
  font-size: var(--fs-md);
  font-weight: var(--font-weight-semi);
  color: var(--text-secondary);
}
/* "Başlamasına …" opening countdown for upcoming rows — slate to match the badge. */
.qak-item-begins {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--fs-md);
  font-weight: var(--font-weight-semi);
  color: var(--catchup-gray-600);
  white-space: nowrap;
}

.qak-item-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 7px;
  flex-shrink: 0;
}
/* An upcoming row renders NO side content (the due date and the CTA both sit behind the
   isUpcoming null-branch), and an empty flex item still costs .qak-item's 12px gap.
   Below 560px it costs more than that: the width: 100% there would wrap the empty div
   onto a line of its own. Unconditional rather than inside the media query, because the
   wasted gap is present at every width. */
.qak-item-side:empty {
  display: none;
}
.qak-item-due {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--fs-md);
  font-weight: var(--font-weight-semi);
  color: var(--text-secondary);
  white-space: nowrap;
}
.qak-item-due img {
  width: 13px;
  height: 13px;
}

/* Narrow screens — this row had NO responsive treatment at all, and the three-column
   shape (52px emblem + body + a flex-shrink:0 side column) starves the middle: measured
   at 375 the body collapsed to 70px, narrower than the status tag's own 91px, so the tag
   spilled straight out of it. That predates the meta rail (reconstructing the previous
   [meta][status] row at 375 overflowed by 29px), but the rail carries a wider label, so
   it is fixed here rather than inherited.
   Same shape .qdd-hero-side uses at its own breakpoint: let the row wrap and give the
   side column a full-width line of its own. */
@media (max-width: 560px) {
  .qak-item {
    flex-wrap: wrap;
  }
  .qak-item-body {
    /* All the width left beside the emblem, so the rail and the ders line get real room
       instead of whatever the un-wrapped side column happened to leave. */
    flex: 1 1 calc(100% - 52px - var(--space-3));
  }
  .qak-item-side {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
}
`;
